/**
 * Guided tour steps — the first-time-user walkthrough of Crosbe AI. Each step
 * navigates to a REAL page (live production data beats screenshots) with a
 * short on-screen blurb and (once generated) an ElevenLabs narration
 * (public/tour/step-N.mp3 — regenerate with scripts/gen-tour-audio.py after
 * editing the SCRIPTS dict there).
 *
 * Curated headline journey across a 45-page app: the BI core (overview →
 * production → quality), the standout end-to-end traceability (cockpit), and
 * the AI (data analyst), closing on Insights. Update this file in the same
 * commit as any change to those pages' layout/data-tour anchors.
 *
 * Portable pattern (github.com/jezweb/walkabout): copy tour/ + components/Tour.tsx
 * into any app and rewrite these steps.
 */
export interface TourStep {
  path: string;
  title: string;
  body: string;
  audio: string;
  /** CSS selector to scroll to + halo while this step is showing. */
  highlight?: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    path: '/',
    title: 'Overview — the plant at a glance',
    body: 'Your command view: production, quality and materials KPIs in one place, pulled live from the plant and Ostendo.',
    audio: '/tour/step-1.mp3',
    highlight: '[data-tour="page-header"]',
  },
  {
    path: '/production',
    title: 'Production — every batch, live',
    body: 'Batches, tonnes and product mix as the plant makes them. Filter by date or product and drill into any batch.',
    audio: '/tour/step-2.mp3',
    highlight: '[data-tour="page-header"]',
  },
  {
    path: '/quality',
    title: 'Quality — SPC, Cpk and Nelson rules',
    body: 'Quality control the way a cement lab thinks about it: control charts, process capability and out-of-spec flags.',
    audio: '/tour/step-3.mp3',
    highlight: '[data-tour="page-header"]',
  },
  {
    path: '/cockpit',
    title: 'Operations Traceability — end-to-end traceability',
    body: 'Trace any customer order back through QC, batching and raw-material POs — honest about the gaps where the data stops.',
    audio: '/tour/step-4.mp3',
    highlight: '[data-tour="page-header"]',
  },
  {
    path: '/analyst',
    title: 'AI Data Analyst — ask your data anything',
    body: 'Ask a plain-English question and the analyst queries the live database, draws the chart, and shows its working.',
    audio: '/tour/step-5.mp3',
    highlight: '[data-tour="page-header"]',
  },
  {
    path: '/insights',
    title: 'Insights — what the numbers are telling you',
    body: 'Surfaced trends and anomalies across production and quality, so the signal finds you. That’s the tour — everything here is live plant data.',
    audio: '/tour/step-6.mp3',
    highlight: '[data-tour="page-header"]',
  },
];

const STORAGE_KEY = 'crosbe:tour';

export function tourSeen(): boolean {
  return Boolean(localStorage.getItem(STORAGE_KEY));
}

export function markTour(state: 'done' | 'dismissed'): void {
  localStorage.setItem(STORAGE_KEY, state);
}
